import { cn } from '@/utils/index';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';

const TooltipProvider = TooltipPrimitive.Provider;

interface TooltipContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const TooltipContext = React.createContext<TooltipContextType | undefined>(undefined);

const Tooltip = ({ children, delayDuration = 0, ...props }: React.ComponentPropsWithRef<typeof TooltipPrimitive.Root>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const contextValue = React.useMemo(() => ({ isOpen, setIsOpen }), [isOpen]);

  return (
    <TooltipContext value={contextValue}>
      <TooltipPrimitive.Root open={isOpen} onOpenChange={setIsOpen} delayDuration={delayDuration} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext>
  );
};

const TooltipTrigger = ({ ...props }: React.ComponentPropsWithRef<typeof TooltipPrimitive.Trigger>) => {
  const context = React.use(TooltipContext);

  if (!context) {
    return null;
  }

  const { setIsOpen } = context;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(true);
    props.onClick?.(e);
  };

  return <TooltipPrimitive.Trigger onClick={handleClick} {...props} />;
};

const TooltipContent = ({
  className,
  sideOffset = 4,
  showArrow = false,
  showCloseButton = false,
  ...props
}: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content> & {
  showArrow?: boolean;
  showCloseButton?: boolean;
}) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'tooltip',
  });

  const context = React.use(TooltipContext);

  if (!context) {
    return null;
  }

  const { setIsOpen } = context;

  return (
    <TooltipPrimitive.Content
      sideOffset={sideOffset}
      className={cn(
        'rounded-input bg-fill-background-neutral animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 text-body-m-regular border-outline-neutral z-50 max-w-[350px] border p-1 shadow-md',
        showCloseButton && 'flex items-start gap-1',
        className,
      )}
      {...props}
    >
      <div className={cn('p-3', showCloseButton && 'pr-0')}>{props.children}</div>
      {showCloseButton && (
        <Button variant='text-secondary' size='icon-m' onClick={() => setIsOpen(false)}>
          <X className='size-6' />
          <span className='sr-only'>{t('closeButton.ariaLabel')}</span>
        </Button>
      )}
      {showArrow && (
        <span className='absolute bottom-px translate-x-[-12px]'>
          <TooltipPrimitive.Arrow className='fill-fill-background-neutral left-0' />
        </span>
      )}
    </TooltipPrimitive.Content>
  );
};

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
